<template>
  <a-config-provider :locale="arcoLocale">
    <div id="app" class="min-h-screen bg-gray-50">
      <!-- 路由视图 -->
      <router-view />
    </div>
  </a-config-provider>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useSystemBrandStore } from '@/stores/system-brand'
import zhCN from '@arco-design/web-vue/es/locale/lang/zh-cn'
import enUS from '@arco-design/web-vue/es/locale/lang/en-us'

const { locale } = useI18n()
const authStore = useAuthStore()
const { user, isAuthenticated } = storeToRefs(authStore)
const systemBrandStore = useSystemBrandStore()

// 根据当前语言切换 Arco Design 的语言包
const arcoLocale = computed(() => {
  return locale.value === 'zh-CN' ? zhCN : enUS
})

/**
 * 回到前台 / 重新聚焦时静默刷新一次品牌配置。
 * 配置只缓存在 localStorage，且没有跨端推送——解决「别的浏览器改了系统 logo，
 * 本浏览器的客户端不更新」的问题。
 */
let lastVisibilityRefreshAt = 0
const refreshBrandOnVisible = () => {
  if (!isAuthenticated.value) return
  if (document.visibilityState !== 'visible') return

  const now = Date.now()
  // 30s 内不重复拉取，避免频繁切窗产生大量请求
  if (now - lastVisibilityRefreshAt < 30_000) return
  lastVisibilityRefreshAt = now
  void systemBrandStore.hydrateFromApi()
}

onMounted(() => {
  if (isAuthenticated.value && user.value) {
    authStore.syncHowxmIdentifyFromStore()
    void systemBrandStore.hydrateFromApi()
  }

  systemBrandStore.applySystemBrand()

  document.addEventListener('visibilitychange', refreshBrandOnVisible)
  window.addEventListener('focus', refreshBrandOnVisible)

  // 应用初始化完成日志
  console.log('[CT_APP] Control Tower 应用组件挂载完成')
  console.log('[CT_APP] 当前语言:', locale.value)
  console.log('[CT_APP] Arco语言包:', arcoLocale.value.locale)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', refreshBrandOnVisible)
  window.removeEventListener('focus', refreshBrandOnVisible)
})

watch(isAuthenticated, authenticated => {
  if (authenticated) {
    void systemBrandStore.hydrateFromApi()
  }
})

watch(
  () => systemBrandStore.effectiveIconUrl,
  () => {
    systemBrandStore.applySystemBrand()
  }
)

watch(
  () => systemBrandStore.effectiveTitle,
  newTitle => {
    const current = document.title
    const sep = ' - '
    const idx = current.lastIndexOf(sep)
    if (idx > 0) {
      document.title = `${current.slice(0, idx)}${sep}${newTitle}`
    }
  }
)
</script>

<style>
/* 全局样式重置 */
#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 高亮样式 */
.highlight {
  background-color: #fff3cd;
  color: #856404;
  padding: 0 2px;
  border-radius: 2px;
}

/* 暗色主题适配 */
[data-theme="dark"] {
  color-scheme: dark;
}

[data-theme="dark"] #app {
  background-color: #141414;
  color: #ffffff;
}

[data-theme="dark"] ::-webkit-scrollbar-track {
  background: #2f2f2f;
}

[data-theme="dark"] ::-webkit-scrollbar-thumb {
  background: #555555;
}

[data-theme="dark"] ::-webkit-scrollbar-thumb:hover {
  background: #777777;
}
</style>
